import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { trackEvent } from "@/lib/track";
import { FacebookLogo } from "@phosphor-icons/react";

const FB_PAGE_SLUG = "__page__"; // national ChambaNOW page-follow fallback

// Local Facebook group invite. Resolves the CLOSEST mapped group within 60mi of the
// given location (a free-form "City, State" string or a ZIP). When no group is in range
// it falls back to a "Follow ChambaNOW on Facebook" invite so the banner is never empty.
// Renders nothing only while loading or when no location is available at all.
export default function FbGroupInvite({ location, country, where = "search", className = "" }) {
  const { lang, market } = useApp();
  const [status, setStatus] = useState("loading"); // loading | group | fallback
  const [group, setGroup] = useState(null);
  const cc = country || (market === "mx" ? "MX" : "US");

  useEffect(() => {
    let cancelled = false;
    const loc = (location || "").trim();
    if (!loc) { setStatus("loading"); setGroup(null); return undefined; }
    setStatus("loading");
    const isZip = /^\d{5}$/.test(loc);
    axios
      .get(`${API}/fb-group/nearest`, { params: isZip ? { zip: loc, country: cc } : { location: loc, country: cc } })
      .then((r) => {
        if (cancelled) return;
        const g = r.data?.group || null;
        setGroup(g);
        setStatus(g ? "group" : "fallback");
      })
      .catch(() => { if (!cancelled) { setGroup(null); setStatus("fallback"); } });
    return () => { cancelled = true; };
  }, [location, cc]);

  if (status === "loading") return null;

  const isGroup = status === "group" && group;
  const slug = isGroup ? (group.slug || "") : FB_PAGE_SLUG;
  const message = isGroup
    ? (lang === "es"
        ? "Únete a nuestro grupo de Facebook local: chambas, trabajadores y contratantes cerca de ti."
        : "Join our local Facebook group: jobs, workers, and hirers near you.")
    : (lang === "es"
        ? "Sigue a ChambaNOW en Facebook para recibir novedades y avisos de trabajo."
        : "Follow ChambaNOW on Facebook for local job updates and notifications.");
  const cta = isGroup
    ? (lang === "es" ? "Unirme al grupo" : "Join group")
    : (lang === "es" ? "Seguir a ChambaNOW" : "Follow ChambaNOW");

  return (
    <a
      href={`${API}/fb/click?slug=${encodeURIComponent(slug)}&src=${encodeURIComponent(where)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => { try { trackEvent("fb_group_click", { slug, where, fallback: !isGroup }); } catch (e) { /* ignore */ } }}
      className={`flex items-center gap-3 rounded-lg border border-[#1877F2]/40 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 transition p-4 ${className}`}
      data-testid={isGroup ? "fb-group-invite" : "fb-page-follow"}
    >
      <FacebookLogo size={28} weight="fill" className="text-[#1877F2] shrink-0" />
      <p className="flex-1 min-w-0 text-sm text-white font-semibold leading-snug">{message}</p>
      <span className="btn-pill-primary whitespace-nowrap" data-testid={isGroup ? "fb-group-invite-join" : "fb-page-follow-cta"}>
        {cta}
      </span>
    </a>
  );
}
